<template>
  <div class="gathering-game">
    <div class="categories">
      <!-- Create a box for each category -->
      <div
        v-for="(category, index) in categories"
        :key="index"
        class="category-box"
        @click="handleCategoryClick(category)"
        @dragover.prevent
        @drop="handleDrop($event, category)"
      >
        <h2 :style="{ color: category.toLowerCase() }">
          {{ category }}
        </h2>
        <p class="progress">
          {{ correctDrops[category].length }} /
          {{ totalItemsPerCategory[category] }}
        </p>
        <!-- Display the correctly dropped items -->
        <div class="dropped-items">
          <span
            v-for="item in correctDrops[category]"
            :key="item.id"
          >
            {{ item.emoji }}
          </span>
        </div>
      </div>
    </div>

    <p
      v-if="message"
      class="message"
    >
      {{ message }}
    </p>

    <div class="draggable-items">
      <!-- Draggable emojis -->
      <div
        v-for="(item) in draggableItems"
        :key="item.id"
        class="draggable-emoji"
        :style="draggedItem === item ? draggedStyle : {}"
        draggable="true"
        @dragstart="handleDrag($event, item)"
        @click="speakOutLoud(item.emoji)"
        @touchstart.prevent="handleTouchStart($event, item)"
        @touchmove.prevent="handleTouchMove($event)"
        @touchend.prevent="handleTouchEnd($event)"
      >
        {{ item.emoji }}
      </div>
    </div>
  </div>
</template>

<script>
import { speechMixin } from "@/mixins/speechMixin"; // update the path as needed

export default {
  mixins: [speechMixin],
  data() {
    const categories = ["Red", "Green", "Orange"];
    const correctDrops = {
      Red: [],
      Green: [],
      Orange: [],
    };
    const totalItemsPerCategory = {
      Red: 0,
      Green: 0,
      Orange: 0,
    };
    return {
      // Define categories
      categories,
      // Define items with their categories
      items: [
        { id: 1, emoji: "🍎", category: "Red" },
        { id: 2, emoji: "🍉", category: "Red" },
        { id: 3, emoji: "🍒", category: "Red" },
        { id: 4, emoji: "🚗", category: "Red" },
        { id: 5, emoji: "🍏", category: "Green" },
        { id: 6, emoji: "🥦", category: "Green" },
        { id: 7, emoji: "🐢", category: "Green" },
        { id: 8, emoji: "🥕", category: "Orange" },
        { id: 9, emoji: "🍊", category: "Orange" },
        { id: 10, emoji: "🦊", category: "Orange" },
      ],
      draggableItems: [], // items with positions
      totalItemsPerCategory, // counts per category
      message: "",
      correctDrops, // Keep track of correct drops for each category
      draggedItem: null,
      draggedStyle: {},
    };
  },
  mounted() {
    // Count total items per category
    this.items.forEach((item) => {
      this.totalItemsPerCategory[item.category]++;
    });

    // Shuffle items
    this.draggableItems = this.items.slice(); // make a copy
    this.draggableItems.sort(() => Math.random() - 0.5);

    // Initialize speech synthesis via the mixin
    if (typeof speechSynthesis === "undefined") {
      console.warn("Speech Synthesis API not supported in this browser.");
    } else {
      speechSynthesis.onvoiceschanged = () => this.setupVoices();
    }
    this.setupVoices();
  },
  methods: {
    handleCategoryClick(category) {
      this.speakOutLoud(category);
    },
    handleDrag(event, item) {
      event.dataTransfer.setData("text/plain", JSON.stringify(item));
    },
    handleDrop(event, targetCategory) {
      const itemData = event.dataTransfer.getData("text/plain");
      if (!itemData) return;
      const item = JSON.parse(itemData);
      if (item.category === targetCategory) {
        if (!this.correctDrops[targetCategory].some((i) => i.id === item.id)) {
          this.correctDrops[targetCategory].push(item);
          this.playSound(
            "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
          );
          // Remove the item from draggableItems
          this.draggableItems = this.draggableItems.filter(
            (i) => i.id !== item.id
          );
          // Check if the game is finished
          let totalCorrect = 0;
          this.categories.forEach((category) => {
            totalCorrect += this.correctDrops[category].length;
          });
          if (totalCorrect === this.items.length) {
            // All items have been placed correctly
            this.message = "🎉 Great Job!";
            this.speakOutLoud("Great Job!");
            setTimeout(() => {
              this.$router.push("/colors");
            }, 4000);
            return;
          }
        } else {
          this.message = "Already placed!";
        }
      } else {
        this.message = "❌ Try again!";
      }
      setTimeout(() => {
        this.message = "";
      }, 3000);
    },
    handleTouchStart(event, item) {
      event.preventDefault(); // Prevent default touch behavior
      const touch = event.touches[0];
      this.draggedItem = item;
      this.draggedStyle = {
        position: "fixed",
        left: `${touch.clientX}px`,
        top: `${touch.clientY}px`,
        transform: "translate(-50%, -50%)",
        zIndex: 1000,
      };
    },
    handleTouchMove(event) {
      event.preventDefault(); // Prevent scrolling
      const touch = event.touches[0];
      this.draggedStyle = {
        ...this.draggedStyle,
        left: `${touch.clientX}px`,
        top: `${touch.clientY}px`,
      };
    },
    handleTouchEnd(event) {
      const touch = event.changedTouches[0];
      const dropZones = this.$el.querySelectorAll(".category-box");
      const touchEndX = touch.clientX;
      const touchEndY = touch.clientY;
      let dropped = false;
      dropZones.forEach((dropZone) => {
        const rect = dropZone.getBoundingClientRect();
        if (
          touchEndX >= rect.left &&
          touchEndX <= rect.right &&
          touchEndY >= rect.top &&
          touchEndY <= rect.bottom
        ) {
          const targetCategory = dropZone.querySelector("h2").innerText;
          this.handleDrop(
            {
              dataTransfer: { getData: () => JSON.stringify(this.draggedItem) },
            },
            targetCategory
          );
          dropped = true;
        }
      });
      if (!dropped) {
        this.message = "❌ Try again!";
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }
      this.draggedItem = null;
      this.draggedStyle = {};
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
  },
};
</script>

<style scoped>
.gathering-game {
  position: relative;
  width: 100%;
  height: 100%;
  user-select: none;
}

.categories {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.category-box {
  width: calc(30% - 1rem);
  min-width: 150px;
  height: auto;
  background-color: white;
  border: 4px solid brown;
  border-radius: 10px;
  position: relative;
  text-align: center;
  padding: 10px;
  cursor: pointer;
  box-sizing: border-box;
}

@media (max-width: 600px) {
  .category-box {
    width: calc(100% - 2rem);
  }
}

.category-box h2 {
  margin: 0;
}

.category-box .progress {
  margin: 5px 0;
  font-weight: bold;
  color: black;
}

.dropped-items {
  width: 100%;
  overflow: auto;
  padding: 5px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: start;
}

.dropped-items span {
  font-size: 2rem;
  margin: 2px;
}

.message {
  text-align: center;
  font-size: 2rem;
  font-weight: bold;
}

.draggable-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin: auto;
  width: 80%;
}

.draggable-emoji {
  cursor: pointer;
  font-size: 3rem;
  margin: 10px;
  transition: transform 0.3s;
  user-select: none;
  touch-action: none;
}

.draggable-emoji:hover {
  transform: scale(1.2);
}

.draggable-emoji:active {
  transform: scale(1.1);
}

.draggable-emoji[style] {
  pointer-events: none;
}
</style>
